// Where the signup app keeps the invite code and the claim secret (plan §6, §16).
//
// - The code arrives in the URL fragment (`signup.dashboard…/#<public_id>.<mac>`; browsers never send
//   the fragment). It moves to this tab's sessionStorage and leaves the address bar at once (the
//   oauthHandoff.js stash-and-strip pattern), so a reload still finds it while a screenshot, a
//   shared screen or the history does not show it. On the signup host index.js runs the strip
//   before Sentry.init (sentrySetup.js): Sentry's history breadcrumbs would otherwise record the
//   pre-strip URL; signup/sentryScrub.ts covers later URLs.
// - The claim secret answered by `claim` stays in localStorage on the signup origin under
//   `cp_signup_claim:<public_id>`: it is what makes this browser the holder (preview answers `yours`).
// - "Connect with <booking system>" comes back to the page with a QUERY string, `?<restech>=connected` or
//   `?<restech>=error&reason=<code>` (never a fragment: a fragment is always the invite code). The outcome moves to
//   sessionStorage (`cp_signup_connect_return`, no secret in it) and leaves the address bar before Sentry starts,
//   like the code; the page reads it on its first render and clears it once mounted (a reload does not show it
//   again; React's StrictMode runs a state initializer twice, so the read itself must not consume it). Before Sentry
//   starts no restech code is known, so every `<key>=connected|error` is kept as a candidate; the page acts only on
//   one whose key is a restech code (pickConnectReturn: the invite's restechs at the resume, `GET restechs` on
//   Connect).
//
// Storage can throw (blocked storage, some private modes): reads then find nothing and writes are
// skipped, which costs a re-claim, never a crash.

const CODE_KEY = 'cp_signup_code';
const CONNECT_RETURN_KEY = 'cp_signup_connect_return';
export const CLAIM_KEY_PREFIX = 'cp_signup_claim:';

function session(): Storage | null {
    try {
        return window.sessionStorage;
    } catch (_e) {
        return null;
    }
}

function local(): Storage | null {
    try {
        return window.localStorage;
    } catch (_e) {
        return null;
    }
}

function decode(raw: string): string {
    try {
        return decodeURIComponent(raw);
    } catch (_e) {
        return raw;
    }
}

/** The code in the URL fragment moves to sessionStorage and the fragment is removed from the URL,
 *  keeping the path and the query. A new fragment replaces an older stash (another invite opened in
 *  this tab). Idempotent. */
export function stashCodeFromUrl(): void {
    const hash = window.location.hash || '';
    if (!hash) {
        return;
    }
    const code = decode(hash.replace(/^#/, '')).trim();
    if (code) {
        try {
            session()?.setItem(CODE_KEY, code);
        } catch (_e) {
            // The page then asks for the link again.
        }
    }
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${window.location.search}`);
}

export function readStashedCode(): string | null {
    try {
        return session()?.getItem(CODE_KEY) || null;
    } catch (_e) {
        return null;
    }
}

export function clearStashedCode(): void {
    try {
        session()?.removeItem(CODE_KEY);
    } catch (_e) {
        // ignore
    }
}

/** The invite's public id: the part of the code before the dot (`<public_id>.<mac>`). */
export function publicIdOf(code: string | null | undefined): string | null {
    if (!code) {
        return null;
    }
    const [publicId] = code.split('.');
    return /^[a-z2-7]{8,16}$/.test(publicId) ? publicId : null;
}

export function readClaimSecret(publicId: string | null): string | null {
    if (!publicId) {
        return null;
    }
    try {
        return local()?.getItem(CLAIM_KEY_PREFIX + publicId) || null;
    } catch (_e) {
        return null;
    }
}

export function storeClaimSecret(publicId: string, secret: string): void {
    try {
        local()?.setItem(CLAIM_KEY_PREFIX + publicId, secret);
    } catch (_e) {
        // This browser then cannot resume after a reload; ops can release the link.
    }
}

export function clearClaimSecret(publicId: string | null): void {
    if (!publicId) {
        return;
    }
    try {
        local()?.removeItem(CLAIM_KEY_PREFIX + publicId);
    } catch (_e) {
        // ignore
    }
}

/** The outcome of "Connect with <booking system>" the page came back with. */
export interface ConnectReturn {
    restech: string;
    outcome: 'connected' | 'error';
    /** A refusal code (`error` only; the contract's `ConnectReturnReason`), else null. */
    reason: string | null;
}

// A restech code as the backend routes it (`[a-z0-9_]{1,32}`), and a reason code.
const RESTECH_PARAM_RE = /^[a-z0-9_]{1,32}$/;
const REASON_RE = /^[a-z0-9_]{1,64}$/;
const OUTCOMES = ['connected', 'error'];

function isConnectReturn(value: any): value is ConnectReturn {
    return !!value && typeof value === 'object' && typeof value.restech === 'string' && RESTECH_PARAM_RE.test(value.restech)
        && OUTCOMES.includes(value.outcome) && (value.reason === null || (typeof value.reason === 'string' && REASON_RE.test(value.reason)));
}

/** At most this many candidates are kept (a real return has one). */
const MAX_CANDIDATES = 4;

/** `?<restech>=connected|error[&reason=<code>]` moves to sessionStorage as candidates (every key shaped like a restech
 *  code with such a value) and those parameters leave the URL (the path, any other parameter and the fragment stay).
 *  Nothing happens without such a parameter. Idempotent. */
export function stashConnectReturnFromUrl(): void {
    const params = new URLSearchParams(window.location.search || '');
    const keys = Array.from(new Set(Array.from(params.keys()))).filter(
        (key) => key !== 'reason' && RESTECH_PARAM_RE.test(key) && OUTCOMES.includes(params.get(key) || ''),
    );
    if (!keys.length) {
        return;
    }
    const reason = params.get('reason');
    const candidates: ConnectReturn[] = keys.slice(0, MAX_CANDIDATES).map((restech) => {
        const outcome = params.get(restech) as ConnectReturn['outcome'];
        return { restech, outcome, reason: outcome === 'error' && reason && REASON_RE.test(reason) ? reason : null };
    });
    try {
        session()?.setItem(CONNECT_RETURN_KEY, JSON.stringify(candidates));
    } catch (_e) {
        // The page then opens without the outcome; the booking-system card says whether it is connected.
    }
    keys.forEach((key) => params.delete(key));
    params.delete('reason');
    const query = params.toString();
    window.history.replaceState(
        window.history.state,
        '',
        `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`,
    );
}

/** The stashed candidates ([] for none or a malformed stash). Reading does not remove them: clearConnectReturn
 *  does. */
export function readConnectReturns(): ConnectReturn[] {
    let raw: string | null = null;
    try {
        raw = session()?.getItem(CONNECT_RETURN_KEY) || null;
    } catch (_e) {
        return [];
    }
    if (!raw) {
        return [];
    }
    try {
        const value = JSON.parse(raw);
        return Array.isArray(value) && value.length <= MAX_CANDIDATES && value.every(isConnectReturn) ? value : [];
    } catch (_e) {
        return [];
    }
}

/** The candidate whose key is one of `codes` (restech codes), or null. `codes` null (every restech: an invite that
 *  names none) accepts the first candidate. */
export function pickConnectReturn(candidates: ConnectReturn[], codes: string[] | null): ConnectReturn | null {
    return candidates.find((c) => codes === null || codes.includes(c.restech)) || null;
}

export function clearConnectReturn(): void {
    try {
        session()?.removeItem(CONNECT_RETURN_KEY);
    } catch (_e) {
        // ignore
    }
}
